import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { AircraftType } from '../domain/models';
import type { AirportView, UICommand, UpdateTicketPriceCommand } from '../domain/models/contracts';
import { AlertsPanel } from './components/AlertsPanel';
import { CabinEditor } from './components/CabinEditor';
import type { CabinEditorCommand } from './components/cabin/cabinEditorTypes';
import { FleetAcquisitionPanel } from './components/fleet/FleetAcquisitionPanel';
import { FleetScheduleGrid, type PendingScheduleLeg } from './components/FleetScheduleGrid';
import { MarketReachPanel } from './components/analytics/MarketReachPanel';
import { MarketSurveyPanel } from './components/analytics/MarketSurveyPanel';
import { DemandMatrixPanel } from './components/analytics/DemandMatrixPanel';
import { RouteAnalyticsPanel } from './components/analytics/RouteAnalyticsPanel';
import { RoutePlannerPanel } from './components/analytics/RoutePlannerPanel';
import { BasePicker } from './components/BasePicker';
import { Header } from './components/Header';
import { HubManager } from './components/infrastructure/HubManager';
import { MarketSlotAuctions } from './components/market/MarketSlotAuctions';
import { OperationsGrid } from './components/OperationsGrid';
import { RouteAnalyticsInspector } from './components/RouteAnalyticsInspector';
import { WorldMap } from './components/map/WorldMap';
import { useSimulationWorker } from './hooks/useSimulationWorker';

type TabId = 'map' | 'fleet' | 'routes' | 'hubs';
type FleetSubTab = 'acquire' | 'cabin';
type RoutesMode = 'network' | 'inspector' | 'planner' | 'market' | 'demand';

const FLEET_SUBTABS: ReadonlyArray<{ readonly id: FleetSubTab; readonly label: string }> = [
  { id: 'acquire', label: 'Acquisition' },
  { id: 'cabin', label: 'Cabin editor' },
];

const TABS: ReadonlyArray<{ readonly id: TabId; readonly label: string }> = [
  { id: 'map', label: 'Map & Operations' },
  { id: 'fleet', label: 'Fleet & Acquisition' },
  { id: 'routes', label: 'Route Analytics & Pricing' },
  { id: 'hubs', label: 'Hubs & Infrastructure' },
];

export function App() {
  const { state, controls } = useSimulationWorker();
  const { dispatch, requestRoutePreview, requestOriginDemand, requestMarketReach, requestMarketSurvey } = controls;
  const [marketAirport, setMarketAirport] = useState<string>('');
  const [plannerPair, setPlannerPair] = useState<{ originId: string; destinationId: string; nonce: number } | null>(null);
  const [tab, setTab] = useState<TabId>('map');
  const [fleetSubTab, setFleetSubTab] = useState<FleetSubTab>('acquire');
  const [routesMode, setRoutesMode] = useState<RoutesMode>('network');
  const [inspectorLegId, setInspectorLegId] = useState<string | undefined>(undefined);
  const [pendingLeg, setPendingLeg] = useState<PendingScheduleLeg | null>(null);
  const clearPendingLeg = useCallback((): void => setPendingLeg(null), []);

  // The planner hands a route to the Gantt scheduler: switch to the fleet tab, which opens the add-leg dialog.
  const scheduleFromPlanner = useCallback((leg: PendingScheduleLeg): void => {
    setPendingLeg(leg);
    setTab('fleet');
  }, []);
  const [acquisitionExpanded, setAcquisitionExpanded] = useState<boolean>(false);

  // Esc closes the full-view acquisition dialog.
  useEffect(() => {
    if (!acquisitionExpanded) return undefined;
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') setAcquisitionExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [acquisitionExpanded]);
  const tabRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});

  const airportsById = useMemo<Readonly<Record<string, AirportView>>>(
    () => Object.fromEntries(state.airports.map((a) => [a.id, a] as const)),
    [state.airports],
  );
  const typesById = useMemo<Readonly<Record<string, AircraftType>>>(
    () => Object.fromEntries(state.aircraftTypes.map((t) => [t.id, t] as const)),
    [state.aircraftTypes],
  );

  const typeNames = useMemo<Readonly<Record<string, string>>>(
    () => Object.fromEntries(state.aircraftTypes.map((t) => [t.id, t.name] as const)),
    [state.aircraftTypes],
  );

  const onCabinCommand = useCallback(
    (command: CabinEditorCommand): void => {
      const mapped: UICommand =
        command.type === 'ACQUIRE_AIRCRAFT'
          ? { type: 'ACQUIRE_AIRCRAFT', typeId: command.typeId, purchaseType: command.purchaseType, cabin: command.cabin }
          : { type: 'REFIT_AIRCRAFT', registration: command.registration, cabin: command.cabin };
      dispatch(mapped);
    },
    [dispatch],
  );

  const onUpdateTicketPrice = useCallback(
    (command: UpdateTicketPriceCommand): void => dispatch(command),
    [dispatch],
  );

  const schedule = useMemo(() => ({ legs: state.legs, maintenance: state.maintenance }), [state.legs, state.maintenance]);

  if (!state.ready) return <BasePicker state={state} controls={controls} />;

  /** Arrow keys move between tabs, as the WAI-ARIA tabs pattern asks. Focus follows the selection. */
  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = TABS.findIndex((t) => t.id === tab);
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = (index + 1) % TABS.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = TABS.length - 1;
    const target = next === null ? undefined : TABS[next];
    if (target === undefined) return;
    event.preventDefault();
    setTab(target.id);
    tabRefs.current[target.id]?.focus();
  };

  return (
    <div className="app">
      <Header state={state} controls={controls} />

      <nav className="exec-tabs" role="tablist" aria-label="Executive views" onKeyDown={onTabKeyDown}>
        {TABS.map((t) => (
          <button
            key={t.id}
            ref={(el) => {
              tabRefs.current[t.id] = el;
            }}
            type="button"
            role="tab"
            id={`exec-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`exec-panel-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            className={`exec-tab-button${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main className="exec-body">
        {/* Every view stays mounted so the map keeps its view and the grids keep their scroll positions. */}
        <section
          role="tabpanel"
          id="exec-panel-map"
          aria-labelledby="exec-tab-map"
          className="exec-tab exec-tab-map"
          hidden={tab !== 'map'}
        >
          <div className="exec-map card" aria-label="Route map">
            <WorldMap
              airports={state.airports}
              legs={state.legs}
              activeFlights={state.activeFlights}
              macroEvents={state.macroEvents}
              hqAirportId={state.hqAirportId}
              running={state.running}
              speedMultiplier={state.speedMultiplier}
              tickReceivedAtMs={state.tickReceivedAtMs}
            />
          </div>

          <aside className="exec-side">
            <section className="card exec-alerts" aria-label="Alerts">
              <AlertsPanel alerts={state.alerts} />
            </section>
            <section className="card exec-pane exec-ops" aria-label="Live operations">
              <OperationsGrid fleet={state.fleet} legs={state.legs} hour={state.hour} />
            </section>
          </aside>
        </section>

        <section
          role="tabpanel"
          id="exec-panel-fleet"
          aria-labelledby="exec-tab-fleet"
          className="exec-tab exec-tab-fleet"
          hidden={tab !== 'fleet'}
        >
          <section className="card exec-pane exec-schedule" aria-label="Fleet schedule">
            <FleetScheduleGrid
              fleet={state.fleet}
              aircraftTypes={typesById}
              airports={airportsById}
              schedule={schedule}
              clock={state.clock}
              activeFlights={state.activeFlights}
              dispatch={dispatch}
              pendingLeg={pendingLeg}
              onPendingLegHandled={clearPendingLeg}
              analytics={state.analytics}
              competitorShares={state.competitorShares}
            />
          </section>

          <section className="card exec-pane exec-lower" aria-label="Acquisition and cabin">
            <div className="exec-subtabs" role="tablist" aria-label="Fleet sections">
              {FLEET_SUBTABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  id={`fleet-subtab-${t.id}`}
                  aria-selected={fleetSubTab === t.id}
                  aria-controls={`fleet-subpanel-${t.id}`}
                  className={`exec-subtab${fleetSubTab === t.id ? ' is-active' : ''}`}
                  onClick={() => setFleetSubTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
              {fleetSubTab === 'acquire' && (
                <button type="button" className="btn btn-small exec-expand" onClick={() => setAcquisitionExpanded(true)}>
                  Open full view
                </button>
              )}
            </div>

            {fleetSubTab === 'acquire' ? (
              <div role="tabpanel" id="fleet-subpanel-acquire" aria-labelledby="fleet-subtab-acquire" className="exec-subpanel">
                {acquisitionExpanded ? (
                  <p className="muted">Showing in the full view. <button type="button" className="btn btn-small" onClick={() => setAcquisitionExpanded(false)}>Bring back here</button></p>
                ) : (
                <FleetAcquisitionPanel
                  acquisitions={state.acquisitions}
                  aircraftTypes={state.aircraftTypes}
                  fleet={state.fleet}
                  legs={state.legs}
                  hour={state.hour}
                  cashUSD={state.ledger?.cashUSD ?? null}
                  onCommand={dispatch}
                />
                )}
              </div>
            ) : (
              <div role="tabpanel" id="fleet-subpanel-cabin" aria-labelledby="fleet-subtab-cabin" className="exec-subpanel">
                {state.aircraftTypes.length > 0 ? (
                  <CabinEditor aircraftTypes={state.aircraftTypes} fleet={state.fleet} onCommand={onCabinCommand} />
                ) : (
                  <p className="muted empty">Waiting for the simulation worker…</p>
                )}
              </div>
            )}
          </section>
        </section>

        <section
          role="tabpanel"
          id="exec-panel-routes"
          aria-labelledby="exec-tab-routes"
          className="exec-tab exec-tab-routes"
          hidden={tab !== 'routes'}
        >
          <section className="card exec-pane exec-routes" aria-label="Route analytics">
            <div className="exec-routes-modes" role="group" aria-label="Route analytics view">
              <button
                type="button"
                aria-pressed={routesMode === 'network'}
                className={`btn btn-small exec-mode${routesMode === 'network' ? ' is-active' : ''}`}
                onClick={() => setRoutesMode('network')}
              >
                Network overview
              </button>
              <button
                type="button"
                aria-pressed={routesMode === 'inspector'}
                className={`btn btn-small exec-mode${routesMode === 'inspector' ? ' is-active' : ''}`}
                onClick={() => setRoutesMode('inspector')}
              >
                Route inspector
              </button>
              <button
                type="button"
                aria-pressed={routesMode === 'planner'}
                className={`btn btn-small exec-mode${routesMode === 'planner' ? ' is-active' : ''}`}
                onClick={() => setRoutesMode('planner')}
              >
                Market opportunity planner
              </button>
              <button
                type="button"
                aria-pressed={routesMode === 'market'}
                className={`btn btn-small exec-mode${routesMode === 'market' ? ' is-active' : ''}`}
                onClick={() => setRoutesMode('market')}
              >
                Market survey
              </button>
              <button
                type="button"
                aria-pressed={routesMode === 'demand'}
                className={`btn btn-small exec-mode${routesMode === 'demand' ? ' is-active' : ''}`}
                onClick={() => setRoutesMode('demand')}
              >
                Demand matrix
              </button>
            </div>
            <div className="exec-routes-body">
              {routesMode === 'network' && (
                <RouteAnalyticsPanel
                  analytics={state.analytics}
                  competitorShares={state.competitorShares}
                  onOpenInspector={(legId) => {
                    setInspectorLegId(legId);
                    setRoutesMode('inspector');
                  }}
                />
              )}
              {routesMode === 'inspector' && (
                <RouteAnalyticsInspector
                  analytics={state.analytics}
                  onUpdateTicketPrice={onUpdateTicketPrice}
                  initialLegId={inspectorLegId}
                />
              )}
              {routesMode === 'market' && (
                <div className="mk-stack">
                  <form
                    className="mk-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const code = marketAirport.trim().toUpperCase();
                      requestMarketSurvey(code === '' ? null : code);
                      if (code !== '') requestMarketReach(code);
                    }}
                  >
                    <input
                      aria-label="Airport code"
                      placeholder="Airport (e.g. BRS)"
                      maxLength={3}
                      value={marketAirport}
                      onChange={(event) => setMarketAirport(event.target.value)}
                    />{' '}
                    <button type="submit" className="btn btn-small">
                      Survey
                    </button>
                  </form>
                  <MarketReachPanel reach={state.marketReach} />
                  <MarketSurveyPanel
                    rows={state.marketSurvey}
                    onPlanRoute={(originId, destinationId) => {
                      setPlannerPair({ originId, destinationId, nonce: (plannerPair?.nonce ?? 0) + 1 });
                      setRoutesMode('planner');
                    }}
                  />
                </div>
              )}
              {routesMode === 'planner' && (
                <RoutePlannerPanel
                  airports={state.airports}
                  aircraftTypes={state.aircraftTypes}
                  fleet={state.fleet}
                  preview={state.routePreview}
                  requestedPair={plannerPair}
                  onRequestPreview={requestRoutePreview}
                  onScheduleLeg={scheduleFromPlanner}
                />
              )}
              {routesMode === 'demand' && (
                <DemandMatrixPanel
                  airports={state.airports}
                  answer={state.originDemand}
                  day={state.completedDays}
                  onRequestRow={requestOriginDemand}
                />
              )}
            </div>
          </section>
        </section>

        <section
          role="tabpanel"
          id="exec-panel-hubs"
          aria-labelledby="exec-tab-hubs"
          className="exec-tab exec-tab-hubs"
          hidden={tab !== 'hubs'}
        >
          <section className="card exec-pane exec-hubs" aria-label="Hubs and infrastructure">
            <HubManager
              hubs={state.hubs}
              airportsById={airportsById}
              cashUSD={state.ledger?.cashUSD ?? null}
              onCommand={dispatch}
              analytics={state.analytics}
              competitorShares={state.competitorShares}
              fleet={state.fleet}
              typeNames={typeNames}
              marketReach={state.marketReach}
              onRequestMarketReach={requestMarketReach}
            />
          </section>

          <section className="card exec-pane exec-market" aria-label="Market and slot auctions">
            <MarketSlotAuctions auctions={state.slotAuctions} day={state.slotAuctionDay} airports={state.airports} />
          </section>
        </section>
      </main>

      {acquisitionExpanded && (
        <div className="exec-modal-scrim" onClick={() => setAcquisitionExpanded(false)}>
          <div
            className="exec-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="exec-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="exec-modal-head">
              <h2 id="exec-modal-title" className="exec-modal-title">Fleet acquisition</h2>
              <button type="button" className="btn btn-small" onClick={() => setAcquisitionExpanded(false)}>
                Close (Esc)
              </button>
            </header>
            <div className="exec-modal-body">
              <FleetAcquisitionPanel
                acquisitions={state.acquisitions}
                aircraftTypes={state.aircraftTypes}
                fleet={state.fleet}
                legs={state.legs}
                hour={state.hour}
                cashUSD={state.ledger?.cashUSD ?? null}
                onCommand={dispatch}
              />
            </div>
          </div>
        </div>
      )}

      {state.lastError !== null && (
        <div className="toast" role="alert">
          <span>{state.lastError}</span>
          <button type="button" className="btn btn-small" onClick={controls.dismissError}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
